import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AccordionFolder {
  id: string;
  name: string;
  color?: string;
  hasNotifications?: boolean;
  count?: string;
  type?: string;
  expanded?: boolean;
  loading?: boolean;
  files?: any[];
  restrictedTo?: any[];
  restricted_to?: any[]; // Firebase property name (snake_case)
  refreshing?: boolean; // New property for refresh state
  consultantEmail?: string; // For consultant folders
  consultantFolder?: boolean; // Flag to identify consultant folders
  facilitatorEmail?: string; // For facilitator folders
  facilitatorFolder?: boolean; // Flag to identify facilitator folders
  facilityName?: string; // For facilitator folders - the facility name
  lastUpdated?: number; // Timestamp for change detection
  parentId?: string; // ID of the parent folder (null for root folders)
  children?: AccordionFolder[]; // Array of child folders
  level?: number; // Depth level in the hierarchy (0 for root)
  hubData?: any; // Original hub data for hub-type folders
  spokeData?: any; // Original spoke data for spoke-type folders
  predefined?: boolean; // Flag to identify predefined/system folders that cannot be deleted
  upload_folder?: boolean; // Flag to identify upload folders
  ownerID?: string; // ID of the owner of the folder
}

@Injectable({
  providedIn: 'root'
})
export class AccordionDataService {
  private foldersSubject = new BehaviorSubject<AccordionFolder[]>([]);
  public folders$ = this.foldersSubject.asObservable();

  // Store all pre-loaded files for quick access
  private allFilesCache: Map<string, any[]> = new Map();

  constructor() {}

  setFolders(folders: AccordionFolder[]): void {
    this.foldersSubject.next(folders);
  }

  // Helper function to find and update folder in hierarchical structure
  private findAndUpdateFolder(folders: AccordionFolder[], folderId: string, files: any[]): boolean {
    for (let i = 0; i < folders.length; i++) {
      const folder = folders[i];

      if (folder.id === folderId) {
        // Split incoming items into subfolders and regular files
        const incomingSubfolders = (files || []).filter(
          (f: any) => f && (f.ftype === 'Folder' || f.type === 'Folder' || f.type === 'folder')
        );
        const incomingFiles = (files || []).filter(
          (f: any) => !(f && (f.ftype === 'Folder' || f.type === 'Folder' || f.type === 'folder'))
        );

        // Update files with only real files (exclude folders-as-files)
        folder.files = incomingFiles;

        // Ensure children array exists
        if (!folder.children) {
          folder.children = [];
        }

        // Helper: normalize/create a child folder entry from a folder-like file
        const toChildFolder = (f: any): AccordionFolder => {
          const childId = f.folderId || f.fileId || f.id;
          return {
            id: childId,
            name: f.fileName || f.name || childId,
            color: f.color,
            hasNotifications: false,
            count: f.fileCount || f.count || '0', // Use fileCount if available, fallback to count
            type: 'folder',
            expanded: false,
            loading: false,
            files: null,
            parentId: folder.id,
            level: (folder.level || 0) + 1,
            children: []
          } as AccordionFolder;
        };

        // Add or update child folders under this parent
        incomingSubfolders.forEach(sf => {
          const childId = sf.folderId || sf.fileId || sf.id;
          if (!childId) {
            return;
          }
          const existingChildIndex = folder.children!.findIndex(c => c.id === childId);
          const childFolder = toChildFolder(sf);
          if (existingChildIndex !== -1) {
            // Update existing child but preserve its expanded/files state
            const existingChild = folder.children![existingChildIndex];
            folder.children![existingChildIndex] = {
              ...existingChild,
              ...childFolder,
              expanded: existingChild.expanded,
              files: existingChild.files
            };
          } else {
            folder.children!.push(childFolder);
          }
        });

        // Update the folder's count to reflect the number of files
        folder.count = incomingFiles.length.toString();

        folder.loading = false;
        folder.refreshing = false;
        return true; // Found and updated
      }

      // Recursively search in children
      if (folder.children && this.findAndUpdateFolder(folder.children, folderId, files)) {
        return true;
      }
    }
    return false; // Not found
  }

  updateFolderFiles(folderId: string, files: any[]): void {
    const currentFolders = this.foldersSubject.value;

    // Find and update the folder
    const found = this.findAndUpdateFolder(currentFolders, folderId, files);

    if (found) {
      this.foldersSubject.next([...currentFolders]);
    } else {
      console.warn('AccordionDataService.updateFolderFiles - Folder not found:', folderId);
    }
  }

  setFolderLoading(folderId: string, loading: boolean): void {
    const currentFolders = this.foldersSubject.value;

    // Helper function to find and update folder in hierarchical structure
    const findAndUpdateFolder = (folders: AccordionFolder[]): boolean => {
      for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];

        if (folder.id === folderId) {
          folder.loading = loading;
          return true; // Found and updated
        }

        // Recursively search in children
        if (folder.children && findAndUpdateFolder(folder.children)) {
          return true;
        }
      }
      return false; // Not found
    };

    // Find and update the folder
    const found = findAndUpdateFolder(currentFolders);

    if (found) {
      this.foldersSubject.next([...currentFolders]);
    } else {
      console.warn('AccordionDataService.setFolderLoading - Folder not found:', folderId);
    }
  }

  setFolderRefreshing(folderId: string, refreshing: boolean): void {
    const currentFolders = this.foldersSubject.value;

    // Helper function to find and update folder in hierarchical structure
    const findAndUpdateFolder = (folders: AccordionFolder[]): boolean => {
      for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];

        if (folder.id === folderId) {
          folder.refreshing = refreshing;
          return true; // Found and updated
        }

        // Recursively search in children
        if (folder.children && findAndUpdateFolder(folder.children)) {
          return true;
        }
      }
      return false; // Not found
    };

    // Find and update the folder
    const found = findAndUpdateFolder(currentFolders);

    if (found) {
      this.foldersSubject.next([...currentFolders]);
    } else {
      console.warn('AccordionDataService.setFolderRefreshing - Folder not found:', folderId);
    }
  }

  updateFolder(updatedFolder: AccordionFolder): void {
    const currentFolders = this.foldersSubject.value;
    const folderIndex = currentFolders.findIndex(folder => folder.id === updatedFolder.id);

    if (folderIndex !== -1) {
      // Preserve expansion state and files
      const existingFolder = currentFolders[folderIndex];

      // Special handling for INDEX folder - get real count from UI paginator
      let finalCount = updatedFolder.count;
      if (updatedFolder.id === 'all' && updatedFolder.name === 'INDEX') {
        // ALWAYS check if we have a recent real-time count for INDEX folder OR if incoming update would decrease count
        const shouldPreserveExisting =
          (existingFolder.lastUpdated && Date.now() - existingFolder.lastUpdated < 30000) ||
          (!updatedFolder.lastUpdated && parseInt(existingFolder.count) > parseInt(updatedFolder.count));

        if (shouldPreserveExisting) {
          // Preserve existing count over any incoming updates that are stale or would decrease count
          finalCount = existingFolder.count;
          const reason = existingFolder.lastUpdated
            ? `recent timestamp (age: ${Date.now() - existingFolder.lastUpdated}ms)`
            : `incoming update would decrease count (${existingFolder.count} > ${updatedFolder.count})`;
        } else {
          try {
            // Try multiple selectors for the paginator
            const selectors = [
              '.mat-mdc-paginator-range-label',
              '.mat-paginator-range-label',
              '.mat-mdc-paginator .mat-mdc-paginator-range-label',
              '[class*="paginator"][class*="range"]',
              '[class*="file"][class*="available"]'
            ];

            let fileTableElement = null;
            let text = '';

            for (const selector of selectors) {
              fileTableElement = document.querySelector(selector);
              if (fileTableElement) {
                text = fileTableElement.textContent || '';

                break;
              }
            }

            if (!fileTableElement) {
              // Try to find any element containing "file(s) available"
              const allElements = Array.from(document.querySelectorAll('*'));
              for (const element of allElements) {
                const elementText = element.textContent || '';
                if (elementText.includes('file(s) available')) {
                  text = elementText;

                  break;
                }
              }
            }

            if (text) {
              // Try multiple regex patterns
              const patterns = [
                /(\d+)\s+file\(s\)\s+available/,
                /(\d+)\s*file\(s\)\s*available/i,
                /(\d+)\s*files?\s*available/i,
                /(\d+)\s*file/i
              ];

              for (const pattern of patterns) {
                const matches = text.match(pattern);
                if (matches && matches[1]) {
                  finalCount = matches[1];

                  break;
                }
              }
            } else {
            }
          } catch (error) {}
        }
      }

      const mergedFolder = {
        ...updatedFolder,
        count: finalCount,
        expanded: existingFolder.expanded,
        files: existingFolder.files,
        loading: existingFolder.loading,
        refreshing: existingFolder.refreshing,
        // Force change detection by adding timestamp
        lastUpdated: Date.now()
      };

      currentFolders[folderIndex] = mergedFolder;
      this.foldersSubject.next([...currentFolders]);
    } else {
      console.warn('AccordionDataService.updateFolder - Folder not found:', updatedFolder.id);
    }
  }

  toggleFolderExpansion(folderId: string): void {
    const currentFolders = this.foldersSubject.value;

    // Helper function to find and toggle folder in hierarchical structure
    const findAndToggleFolder = (folders: AccordionFolder[]): boolean => {
      for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];

        if (folder.id === folderId) {
          const willExpand = !folder.expanded;

          if (willExpand) {
            // For hierarchical folders, only close other folders at the same level
            // Keep parent folders open
            const closeOtherFoldersAtSameLevel = (foldersToClose: AccordionFolder[], targetFolder: AccordionFolder) => {
              foldersToClose.forEach(f => {
                // Don't close the target folder or its parent folders
                if (f.id !== folderId && f.id !== targetFolder.parentId) {
                  f.expanded = false;
                }
                if (f.children) {
                  closeOtherFoldersAtSameLevel(f.children, targetFolder);
                }
              });
            };
            closeOtherFoldersAtSameLevel(currentFolders, folder);

            // Then expand the target folder
            folders[i].expanded = true;
          } else {
            // Just close the target folder
            folders[i].expanded = false;
          }

          return true; // Found and toggled
        }

        // Recursively search in children
        if (folder.children && findAndToggleFolder(folder.children)) {
          return true;
        }
      }
      return false; // Not found
    };

    // Find and toggle the folder
    const found = findAndToggleFolder(currentFolders);

    if (found) {
      this.foldersSubject.next([...currentFolders]);
    } else {
      console.warn('AccordionDataService.toggleFolderExpansion - Folder not found:', folderId);
    }
  }

  // New methods for pre-loading and caching
  preloadAllFiles(allFiles: any[]): void {
    // Clear existing cache
    this.allFilesCache.clear();

    // Group files by folder
    const filesByFolder = new Map<string, any[]>();

    // Initialize with 'all' folder for INDEX
    filesByFolder.set('all', []);

    allFiles.forEach(file => {
      const folderId = file.parentFolder || file.folderId || 'all';

      // Add to specific folder
      if (!filesByFolder.has(folderId)) {
        filesByFolder.set(folderId, []);
      }
      filesByFolder.get(folderId)!.push(file);

      // Also add to 'all' folder for INDEX view
      filesByFolder.get('all')!.push(file);
    });

    // Store in cache
    filesByFolder.forEach((files, folderId) => {
      this.allFilesCache.set(folderId, files);
    });
  }

  getCachedFiles(folderId: string): any[] | null {
    return this.allFilesCache.get(folderId) || null;
  }

  clearCachedFiles(folderId: string): void {
    this.allFilesCache.delete(folderId);
  }

  getAllCachedFiles(): any[] {
    return this.allFilesCache.get('all') || [];
  }

  clearCache(): void {
    this.allFilesCache.clear();
  }

  /** Reset folder.files to null for every folder (recursive), so the next
   *  expand triggers a fresh backend fetch.  Also clears the allFilesCache. */
  invalidateFiles(): void {
    const resetFiles = (folder: AccordionFolder): AccordionFolder => ({
      ...folder,
      files: null,
      children: folder.children?.map(resetFiles)
    });
    this.foldersSubject.next(this.foldersSubject.value.map(resetFiles));
    this.allFilesCache.clear();
  }

  isFolderRefreshing(folderId: string): boolean {
    const currentFolders = this.foldersSubject.value;
    const folder = currentFolders.find(f => f.id === folderId);
    return folder ? folder.refreshing || false : false;
  }

  getCurrentFolders(userOwnerID?: string): AccordionFolder[] {
    if (userOwnerID) {
      return this.foldersSubject.value.filter(folder => folder.ownerID === userOwnerID);
    } else {
      return this.foldersSubject.value;
    }
  }

  addFolder(newFolder: AccordionFolder): void {
    const folders = this.foldersSubject.value;

    if (newFolder.parentId) {
      this.addFolderToParent(folders, newFolder);
    } else {
      newFolder.level = 0;
      newFolder.children = [];
      folders.push(newFolder);
    }

    this.foldersSubject.next(folders.slice());
  }

  private addFolderToParent(folders: AccordionFolder[], newFolder: AccordionFolder): void {
    for (let folder of folders) {
      if (folder.id === newFolder.parentId) {
        newFolder.level = (folder.level || 0) + 1;
        newFolder.children = [];
        if (!folder.children) {
          folder.children = [];
        }
        folder.children.push(newFolder);
        return;
      }
      if (folder.children) {
        this.addFolderToParent(folder.children, newFolder);
      }
    }
  }

  getFlatFolderList(): AccordionFolder[] {
    const flatList: AccordionFolder[] = [];
    this.flattenFolders(this.foldersSubject.value, flatList);
    return flatList;
  }

  private flattenFolders(folders: AccordionFolder[], flatList: AccordionFolder[]): void {
    folders.forEach(folder => {
      flatList.push(folder);
      if (folder.children) {
        this.flattenFolders(folder.children, flatList);
      }
    });
  }

  getRootFolders(): AccordionFolder[] {
    // Return only root folders (those without parentId or with parentId null/empty)
    // But also include folders that have children, as they should be displayed in the hierarchy
    return this.foldersSubject.value.filter(
      folder => !folder.parentId || folder.parentId === null || folder.parentId === ''
    );
  }

  /**
   * Flatten hierarchical folder structure for processing
   * @param folders Array of folders (may contain nested children)
   * @returns Flattened array of all folders with proper parentId relationships
   */
  flattenFolderHierarchy(folders: AccordionFolder[]): AccordionFolder[] {
    const flattened: AccordionFolder[] = [];

    const processFolder = (folder: AccordionFolder, parentId: string | null = null, level: number = 0) => {
      // Create a copy of the folder with proper hierarchy properties
      const folderCopy: AccordionFolder = {
        ...folder,
        parentId: parentId,
        level: level,
        children: undefined // Remove children from flattened structure
      };

      flattened.push(folderCopy);

      // Process children if they exist
      if (folder.children && folder.children.length > 0) {
        folder.children.forEach(child => {
          processFolder(child, folder.id, level + 1);
        });
      }
    };

    folders.forEach(folder => {
      processFolder(folder);
    });

    return flattened;
  }

  findFolderById(folderId: string): AccordionFolder | null {
    return this.findFolderInList(this.foldersSubject.value, folderId);
  }

  private findFolderInList(folders: AccordionFolder[], folderId: string): AccordionFolder | null {
    for (let folder of folders) {
      if (folder.id === folderId) {
        return folder;
      }
      if (folder.children) {
        const found = this.findFolderInList(folder.children, folderId);
        if (found) return found;
      }
    }
    return null;
  }

  removeFolder(folderId: string): void {
    const currentFolders = this.foldersSubject.value;
    this.removeFolderFromList(currentFolders, folderId);
    this.foldersSubject.next([...currentFolders]);
  }

  private removeFolderFromList(folders: AccordionFolder[], folderId: string): boolean {
    for (let i = 0; i < folders.length; i++) {
      if (folders[i].id === folderId) {
        folders.splice(i, 1);
        return true;
      }
      if (folders[i].children) {
        if (this.removeFolderFromList(folders[i].children!, folderId)) {
          return true;
        }
      }
    }
    return false;
  }
}
